/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as $ from "jquery";
import {UiField} from "./UiField";
import {UiColorPickerConfig, UiColorPickerSubEventSource} from "../../generated/UiColorPickerConfig";
import {TeamAppsUiContext} from "../TeamAppsUiContext";
import {TeamAppsUiComponentRegistry} from "../TeamAppsUiComponentRegistry";
import {UiFieldEditingMode} from "../../generated/UiFieldEditingMode";
import {createUiColorConfig, UiColorConfig} from "../../generated/UiColorConfig";
import {create as createPickr, HSVaColor, Pickr} from "pickr-widget";
import {createUiColorCssString} from "../util/CssFormatUtil";
import {executeWhenAttached} from "../util/ExecuteWhenAttached";

/**
 * @author Yann Massard (yamass@gmail.com)
 */
export class UiColorPicker extends UiField<UiColorPickerConfig, UiColorConfig> implements UiColorPickerSubEventSource {
	private $main: JQuery;
	private pickr: Pickr;
	private doNotCommit: boolean;

	protected initialize(config: UiColorPickerConfig, targetElement: Element, context: TeamAppsUiContext) {
		this.$main = $(`<div class="UiColorPicker"><div class="pickr"></div></div>`);
		this.doNotCommit = true;
		this.pickr = createPickr({
			el: this.$main.find('.pickr')[0],
			appendToBody: true,
			position: "middle", // initially not positioned correctly TODO create issue

			default: createUiColorCssString(config.defaultColor),

			components: {

				preview: true,
				opacity: true,
				hue: true,

				output: {
					hex: true,
					rgba: true,
					hsva: true,
					input: true
				}
			},

			onChange: (color: HSVaColor) => {
				// TODO option for that?
			},
			onSave: (hsva, instance) => {
				this.commit();
			},

			strings: {
				save: config.saveButtonCaption,
				clear: config.clearButtonCaption
			}
		});
		this.doNotCommit = false;
	}

	isValidData(v: UiColorConfig): boolean {
		return v == null || (v.red != null && v.green != null && v.blue != null && v.alpha != null);
	}

	commit(forceEvenIfNotChanged?: boolean): boolean {
		if (!this.doNotCommit) {
			return super.commit(forceEvenIfNotChanged);
		} else {
			return false;
		}
	}

	@executeWhenAttached()
	protected displayCommittedValue(): void {
		let committedValue = this.getCommittedValue();
		if (committedValue != null && committedValue.alpha === 1) { // TODO https://github.com/Simonwep/pickr/issues/19
			committedValue = {...committedValue, alpha: .999999};
		}
		let colorString = committedValue != null ? createUiColorCssString(committedValue) : createUiColorCssString(this.getDefaultValue());

		try {
			this.doNotCommit = true;
			this.pickr.setColor(colorString);
		} finally {
			this.doNotCommit = false;
		}
	}

	getDefaultValue(): UiColorConfig {
		return this._config.defaultColor;
	}

	getFocusableElement(): JQuery {
		return null;
	}

	getMainDomElement(): JQuery {
		return this.$main;
	}

	getTransientValue(): UiColorConfig {
		let rgba = this.pickr.getColor().toRGBA();
		return createUiColorConfig(rgba[0], rgba[1], rgba[2], {alpha: rgba[3] || 1});
	}

	protected onEditingModeChanged(editingMode: UiFieldEditingMode): void {
		// TODO
	}

	valuesChanged(v1: UiColorConfig, v2: UiColorConfig): boolean {
		return (v1 == null) !== (v2 == null)
			|| (v1 != null && v2 != null)
			&& (v1.red !== v2.red
				|| v1.green !== v2.green
				|| v1.blue !== v2.blue
				|| v1.alpha !== v2.alpha);
	}

	destroy(): void {
		this.pickr.destroyAndRemove();
	}

}

TeamAppsUiComponentRegistry.registerFieldClass("UiColorPicker", UiColorPicker);